import React, {useEffect, useState } from "react";
import { Link } from "react-router-dom";


const BmiBriCalculator = () => {

  const [age, setAge] = useState("");
  const [gender, setGender] = useState("Male");
  const [unit, setUnit] = useState("metric");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [waist, setWaist] = useState("");
  const [waistToHeight, setWaistToHeight] = useState("");
  const [result, setResult] = useState({
    bmi: null,
    bmiCategory: "",
    bri: null,
    briCategory: "",
    abdominalObesity: "No",
  });


  const calculate = () => {
    let heightInCm = unit === "metric" ? height : height * 2.54;
    let weightInKg = unit === "metric" ? weight : weight * 0.453592;
    let waistInCm = unit === "metric" ? waist : waist * 2.54;
    const waistInM = waistInCm / 100; // Convert waist from cm to meters
    const heightInM = heightInCm / 100; // Convert height from cm to meters

    const bmi = (weightInKg / (heightInM ** 2)).toFixed(2);

    let bmiCategory = "";
    if (bmi < 18.5) bmiCategory = "Underweight";
    else if (bmi < 25) bmiCategory = "Normal weight";
    else if (bmi < 30) bmiCategory = "Overweight";
    else bmiCategory = "Obesity";

    const bri = (
      364.2 -
      365.5 * Math.sqrt(1 - (waistInM / (2 * Math.PI)) ** 2 / (0.5 * heightInM) ** 2)
    ).toFixed(2);

    let briCategory = "";
    if (bri < 3.41) briCategory = "Very lean";
    else if (bri < 4.45) briCategory = "Lean to average";
    else if (bri < 5.46) briCategory = "Average";
    else if (bri < 6.91) briCategory = "Above average";
    else briCategory = "High";

    let abdominalObesity = "No";
    if (
      (gender === "Male" && waistInCm > 94) ||
      (gender === "Female" && waistInCm > 80) ||
      waistToHeight > 0.5
    ) {
      abdominalObesity = "Yes";
    }
    setResult({ bmi, bmiCategory, bri, briCategory, abdominalObesity });
  };

  const handleHeightChange = (e) => {
    const newHeight = e.target.value;
    setHeight(newHeight);
    
    if (newHeight && waist) {
      const heightInCm = unit === "metric" ? newHeight : newHeight * 2.54;
      const waistInCm = unit === "metric" ? waist : waist * 2.54;
      const ratio = (waistInCm / heightInCm).toFixed(2);
      setWaistToHeight(ratio);
    } else {
      setWaistToHeight("");
    }
  };

  const handleWaistChange = (e) => {
    const newWaist = e.target.value;
    setWaist(newWaist);
    
    if (height && newWaist) {
      const heightInCm = unit === "metric" ? height : height * 2.54;
      const waistInCm = unit === "metric" ? newWaist : newWaist * 2.54;
      const ratio = (waistInCm / heightInCm).toFixed(2);
      setWaistToHeight(ratio);
    } else {
      setWaistToHeight("");
    }
  };

  const getProgressBar = (category, type) => {
    const categories =
      type === "bmi"
        ? ["Underweight", "Normal weight", "Overweight", "Obesity"]
        : ["Very lean", "Lean to average", "Average", "Above average", "High"];

    const colors =
      type === "bmi"
        ? ["#c6e377", "#4caf50", "#ffa726", "#ef5350"]
        : ["#c6e377", "#8bc34a", "#4caf50", "#ffa726", "#ef5350"];

    const categoryIndex = categories.indexOf(category);
    const arrowPosition = (categoryIndex + 0.5) * (100 / categories.length);

    // Function to handle text display for mobile
    const getCategoryLabel = (text) => {
      if (window.innerWidth <= 576 && text === "Above average") {
        return "Above\naverage";
      }
      if (window.innerWidth <= 576 && text === "Normal weight") {
        return "Normal\nweight";
      }
      return text;
    };

    return (
      <div style={{ position: "relative", margin: "15px 0", width: "100%" }}>
        {/* Progress Bar */}
        <div
          style={{
            display: "flex",
            height: "25px",
            width: "100%",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          {categories.map((_, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                backgroundColor: colors[index],
              }}
            ></div>
          ))}
        </div>

        {/* Moving Arrow */}
        <div
          style={{
            position: "absolute",
            top: "-8px",
            left: `${arrowPosition}%`,
            transform: "translateX(-50%)",
            transition: "left 1s ease-in-out",
            width: "0",
            height: "0",
            borderLeft: "8px solid transparent",
            borderRight: "8px solid transparent",
            borderTop: "8px solid black",
          }}
        ></div>

        {/* Category Labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "10px",
            width: "100%",
          }}
        >
          {categories.map((cat, index) => (
            <div
              key={index}
              style={{
                flex: 1,
                textAlign: "center",
                padding: "0 2px",
                fontSize: "calc(0.65rem + 0.2vw)", // Smaller base font size
                whiteSpace: "pre-line", // Allow line breaks
                lineHeight: "1.1", // Tighter line height
                wordBreak: "break-word", // Break words if needed
              }}
            >
              {getCategoryLabel(cat)}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const isFormValid = age && height && weight && waist;

  return (
    <>
      <section className="fresh-vegetable-section section-lg-space">
        <div className="container-fluid-lg">
          <div className="card">
            <div className="card-body p-3 p-md-5">
              <h1 className="font-lg mb-4 fw-semibold text-center">BRI and BMI Calculator</h1>
              <div className="row flex-column flex-xl-row">
                <div className="col-12 col-xl-7 mb-4 mb-xl-0">
                  <div className="mx-auto" style={{ maxWidth: '600px' }}>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        calculate();
                      }}
                    >
                      <div className="mb-3">
                        <label htmlFor="age" className="form-label font-sm">
                          Age (yrs):
                        </label>
                        <input
                          type="text"
                          className="form-control font-sm"
                          id="age"
                          name="age"
                          placeholder="Enter your age (yrs)"
                          value={age || ""}
                          onChange={(e) => setAge(e.target.value)}
                          onFocus={(e) => e.preventDefault()}
                          required
                        />
                      </div>


                      <div className="mb-3 font-sm">
                        <label htmlFor="gender" className="form-label font-sm">
                          Gender:
                        </label>
                        <select
                          className="form-select font-sm"
                          id="gender"
                          name="gender"
                          value={gender}
                          onChange={(e) => setGender(e.target.value)}
                          required
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label htmlFor="height" className="form-label font-sm">
                          Height (cm):
                        </label>
                        <input
                          type="number"
                          className="form-control font-sm"
                          id="height"
                          name="height"
                          placeholder={
                            unit === "metric"
                              ? "Enter height in cm"
                              : "Enter height in inches"
                          }
                          value={height}
                          onChange={(e) => handleHeightChange(e) }
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="weight" className="form-label font-sm">
                          Weight (kg):
                        </label>
                        <input
                          type="number"
                          className="form-control font-sm"
                          id="weight"
                          name="weight"
                          placeholder={
                            unit === "metric"
                              ? "Enter weight in kg"
                              : "Enter weight in lbs"
                          }
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="waist" className="form-label font-sm">
                          Waist Circumference (cm):
                        </label>
                        <input
                          type="number"
                          className="form-control font-sm"
                          id="waist"
                          name="waist"
                          placeholder={
                            unit === "metric"
                              ? "Enter waist in cm"
                              : "Enter waist in inches"
                          }
                          value={waist}
                          onChange={(e) =>  handleWaistChange(e)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="waistToHeight" className="form-label font-sm">
                          Waist to Height ratio:
                        </label>
                        <input
                          type="number"
                          className="form-control font-sm"
                          id="waistToHeight"
                          placeholder="Waist to height ratio"
                          value={waistToHeight}
                          name="waistToHeight"
                          disabled
                        />
                      </div>
                      <div className="text-center">
                        <button
                          type="submit"
                          className="btn_get_started btns btn-animation-blue font-sm"
                        >
                          Calculate
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="col-12 col-xl-5">
                  <div className="mx-auto" style={{ maxWidth: '600px' }}>
                    {result && (result.bmi || result.bmiCategory || result.bri || result.briCategory) ? (
                      <div className="result-calculator-section p-3">
                        <div className="font-md mb-3 fw-semibold text-center">
                          Your Body Composition Results
                        </div>
                        
                        <div className="card-calculator mb-3 p-3">
                          <div className="font-smd my-2">
                            <strong>Body Mass Index (BMI) : </strong> 
                            <span className="d-inline-block">{result.bmi || ""}</span>
                            {result.bmiCategory === "Overweight" || result.bmiCategory === "Obesity" ? (
                              <p className="mt-2 text-wrap">
                                {result.bmiCategory === "Overweight" ? (
                                  <p>Your score indicates overweight. Reduce Carbohydrates....Reduce BMI !!! It's time to <Link to={'/shop'} style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: 'inline-block', 
                                    transition: 'transform 0.2s ease-in-out'
                                  }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}><span style={{ color: 'var(--theme-color1)', fontWeight: 'bold' }}>Red</span>uce <span style={{ color: 'var(--theme-color)', fontWeight: 'bold' }}>Carb </span>ohydrates</Link> mindfully .......!!!</p>
                                ) : result.bmiCategory === "Obesity" ? (
                                  <p>Your score indicates obesity. Reduce Carbohydrates....Reduce BMI !!! It's time to <Link to={'/shop'} style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: 'inline-block',
                                    transition: 'transform 0.2s ease-in-out'
                                  }}
                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}><span style={{ color: 'var(--theme-color1)', fontWeight: 'bold' }}>Red</span>uce <span style={{ color: 'var(--theme-color)', fontWeight: 'bold' }}>Carb </span>ohydrates</Link> mindfully .......!!!</p>
                                ) : null}
                              </p>
                            ) : null}
                          </div>
                          {result.bmiCategory && getProgressBar(result.bmiCategory, "bmi")}
                        </div>

                        <div className="card-calculator mb-3 p-3">
                          <div className="font-smd mb-2">
                            <strong>Body Roundness Index (BRI) : </strong>
                            <span className="d-inline-block">{result.bri || ""}</span>
                          </div>
                          {result.briCategory && getProgressBar(result.briCategory, "bri")}
                        </div>

                        <div className="card-calculator p-3">
                          <div className="font-smd mb-2">
                            <strong>Abdominal Obesity : </strong>
                            <span className="color-blue-calculator fw-semibold d-inline-block">
                              {result.abdominalObesity}
                            </span>
                            {result.abdominalObesity === "Yes" && (
                              <p className="mt-2 text-wrap">
                                Carbohydrate restricted diet is a scientific strategy to help abdominal obesity reversal. 
                                It's time to{" "}
                                <Link
                                  to={'/shop'}
                                  style={{
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    display: 'inline-block',
                                    transition: 'transform 0.2s ease-in-out',
                                  }}
                                  onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                                  onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                                >
                                  <span style={{ color: 'var(--theme-color1)', fontWeight: 'bold' }}>Red</span>
                                  uce{" "}
                                  <span style={{ color: 'var(--theme-color)', fontWeight: 'bold' }}>Carb </span>
                                  ohydrates
                                </Link>{" "}
                                mindfully ....!!!
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mt-3 text-center">
                          <p className="mb-0">
                            It is also important to note the role of physical activity for people who are overweight, abdominal obesity, and pre-diabetes
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="no-result-message text-center p-3">
                        <div className="font-sm">Kindly enter the details to get the result.</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
    
  
  );
};

export default BmiBriCalculator;